import { memo, useMemo } from "react";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { selectPositionLtpsSignature } from "../../selectors/security";
import { useStyles } from "./styles";
import { GroupedPosition } from "../../reducer/positions.reducer";
import { Typography } from "@mui/material";
import { getPositionSecurityKeys, parseLtpsSignature } from "../../util/positionLtpsSignature";

export type ProfitCellProps = {
    position: GroupedPosition,
}

const ProfitCell: React.FC<ProfitCellProps> = ({position}) => {

    const {classes, cx} = useStyles();
    const securityKey = getSecurityKey(position.security.exchange, position.security.code);
    const isBasket = (position.children?.length ?? 0) > 1;
    const positionSecurityKeys = useMemo(
        () => (isBasket ? getPositionSecurityKeys(position) : [securityKey]),
        [position, securityKey, isBasket]
    );
    const ltpsSignature = useSelector(selectPositionLtpsSignature(positionSecurityKeys));

    const ltpsByKey = useMemo(() => {
        const map = new Map<string, number>();
        const ltps = parseLtpsSignature(ltpsSignature);
        positionSecurityKeys.forEach((key, i) => {
            map.set(key, ltps[i] ?? 0);
        });
        return map;
    }, [ltpsSignature, positionSecurityKeys]);

    // For basket/strategy positions
    const basketProfitData = useMemo(() => {
        const { security, realizedProfit, children = [] } = position;
        if (!isBasket) return { unrealized: 0, realized: 0, total: 0 };

        let unrealizedProfit = 0;
        children.forEach(childPos => {
            const childSecKey = getSecurityKey(childPos.security.exchange, childPos.security.code);
            const ltp = ltpsByKey.get(childSecKey) ?? 0;
            unrealizedProfit += (ltp - childPos.avgPrice) * childPos.quantity;
        });

        const unrealizedConverted = unrealizedProfit / (security.priceMultiplier ?? 100);
        const realizedConverted = realizedProfit / (security.priceMultiplier ?? 100);

        return {
            unrealized: unrealizedConverted,
            realized: realizedConverted,
            total: unrealizedConverted + realizedConverted
        };
    }, [position, isBasket, ltpsByKey])

    // For single positions
    const singleProfitData = useMemo(() => {
        const { security, avgPrice, realizedProfit, quantity } = position;
        if (isBasket) return { unrealized: 0, realized: 0, total: 0 };

        const ltp = ltpsByKey.get(securityKey) ?? 0;
        const unrealizedConverted = ((ltp - avgPrice) * quantity) / (security.priceMultiplier ?? 100);
        const realizedConverted = realizedProfit / (security.priceMultiplier ?? 100);

        return {
            unrealized: unrealizedConverted,
            realized: realizedConverted,
            total: unrealizedConverted + realizedConverted
        };
    }, [position, isBasket, ltpsByKey, securityKey]);

    const profitData = useMemo(() => isBasket ? basketProfitData : singleProfitData, [basketProfitData, singleProfitData, isBasket]);

    const isBasketFullySquaredOff = useMemo(() => {
        if (!isBasket) return false;
        return position.children.every(child => child.quantity === 0);
    }, [position.children, isBasket]);
    
    const displayProfit = isBasket
    ? (isBasketFullySquaredOff ? profitData.realized : profitData.unrealized)
    : (position.quantity !== 0 ? profitData.unrealized : profitData.realized);
    const displayColor = displayProfit > 0 ? classes.profitColor : displayProfit < 0 ? classes.lossColor : undefined;

    return <>
        <Typography
            variant="body2"
            className={cx(displayColor)}
            style={{ textAlign: 'right' }}
        >
            {displayProfit}
        </Typography>
    </>;
}


export default memo(ProfitCell);
